import React, { useState } from "react";

const getAvatarColor = (name) => {
  const colors = [
    '#1DA1F2', '#E91E8C', '#FF6B35', '#4CAF50', '#9C27B0',
    '#FF5722', '#2196F3', '#009688', '#FF9800', '#607D8B'
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Initiales d'une ENTITÉ (personne, association) — source unique.
 *
 * DOCTRINE (contrat maître, tranchée au lot D) : une entité sans avatar
 * n'affiche PAS un placeholder « photo » et surtout pas un « ? » — elle
 * affiche ses initiales sur une couleur stable. Et quand il n'y a même pas de
 * nom, elle n'affiche RIEN : le disque coloré suffit, il ne pose pas de
 * question à l'utilisateur. C'est pourquoi cette fonction rend "" et non "?".
 *
 * DEUX DÉFAUTS RÉELS CORRIGÉS ICI (revue adversariale du lot D) :
 *  1. `split(' ')` sur un nom à espaces multiples (« Croix  Rouge », très
 *     courant dans les noms d'associations saisis à la main, et systématique
 *     avec une espace insécable) produisait parts = ["Croix", "", "Rouge"] :
 *     `parts[1][0]` valait `undefined`, et la concaténation rendait
 *     littéralement « CUNDEFINED » dans le disque. `\s+` + `filter(Boolean)`
 *     ferme le cas pour tous les appelants d'un coup.
 *  2. le repli `'?'` — le glyphe que le contrat proscrit — vivait DANS le
 *     helper partagé. Le corriger chez trois appelants laissait les deux
 *     autres (AssociationCard, tunnel de publication) afficher le « ? ».
 *     Une doctrine se tient à la SOURCE, pas dans chaque appelant.
 */
const getInitials = (name) => {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
};

/**
 * Phase 27G.6 — Lecture robuste de l'URL avatar quel que soit le
 * nom de champ utilisé par le backend / le contexte d'affichage.
 *
 * Backend Gively renvoie selon les routes :
 *   * ``users.avatar`` (profil principal)
 *   * ``users.avatar_url`` (variante REST)
 *   * ``users.profile_image`` / ``users.profile_picture`` (legacy)
 *   * ``users.picture`` (Google OAuth)
 *   * ``users.image`` (variantes simples)
 *
 * Quand on passe un ``listing`` (page annonce, checkout récap), les
 * champs sont préfixés ``seller_`` :
 *   * ``listing.seller_avatar`` / ``seller_avatar_url`` / ``seller_image``
 *
 * Et pour le côté admin SalesPage où on affiche l'acheteur :
 *   * ``buyer.avatar`` / ``buyer_avatar`` / ``buyer_avatar_url``
 *
 * Cette fonction prend tout user-like et renvoie la première URL
 * non-vide trouvée. Maintient la cohérence visuelle partout.
 */
const _resolveAvatarUrl = (user) => {
  if (!user || typeof user !== "object") return null;
  return (
    user.avatar ||
    user.avatar_url ||
    user.picture ||
    user.profile_image ||
    user.profile_picture ||
    user.image ||
    user.photo ||
    // Variantes contextuelles ``seller_*`` (page annonce, checkout) +
    // ``buyer_*`` (sales/admin) — Phase 6+ rajoute ``seller_picture``
    // qui est désormais exposé par /api/listings/{id} (cf. enrichissement
    // backend).
    user.seller_avatar ||
    user.seller_avatar_url ||
    user.seller_image ||
    user.seller_picture ||
    user.seller_photo ||
    user.buyer_avatar ||
    user.buyer_avatar_url ||
    user.buyer_picture ||
    null
  );
};

/**
 * Phase 27G.6 — Lecture du nom : ``user.name`` puis variantes
 * ``seller_name`` / ``buyer_name`` pour les contextes annonce / sales.
 */
const _resolveName = (user) => {
  if (!user || typeof user !== "object") return "";
  return (
    user.name ||
    user.full_name ||
    user.display_name ||
    user.seller_name ||
    user.buyer_name ||
    ""
  );
};

const UserAvatar = ({ user, size = 'md', className = '', testId }) => {
  const sizes = { sm: 'w-6 h-6', md: 'w-8 h-8', lg: 'w-12 h-12', xl: 'w-16 h-16' };
  const sizeClass = sizes[size] || sizes.md;
  const [errored, setErrored] = useState(false);

  const url = _resolveAvatarUrl(user);
  const name = _resolveName(user);

  if (url && !errored) {
    return (
      <img
        src={url}
        alt={name || "Avatar"}
        loading="lazy"
        decoding="async"
        onError={() => setErrored(true)}
        className={`${sizeClass} rounded-full object-cover bg-slate-100 flex-shrink-0 ${className}`}
        data-testid={testId || "user-avatar"}
      />
    );
  }

  const color = getAvatarColor(name);
  const initials = getInitials(name);
  return (
    <div
      className={`${sizeClass} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${className}`}
      style={{ background: color }}
      data-testid={testId || "user-avatar-fallback"}
      aria-label={name || "Utilisateur"}
    >
      {initials}
    </div>
  );
};

export {
  getAvatarColor,
  getInitials,
  UserAvatar,
  _resolveAvatarUrl,
  _resolveName,
};
export default UserAvatar;
